import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {GroupService} from '../../../services/group-details/group.service';
import {UserService} from '../../../services/user.service';
import {
  ActionDetails,
  ActionSource,
  EventTrackerService,
  Page,
  TargetType,
  UserActionType
} from '../../../services/event-tracker/event-tracker.service';
import {AutoGroupDropdown} from '../../../../models/group-settings';
import {AutoGroupSourcesComponent} from './auto-group-sources/auto-group-sources.component';
import {AutoGroupFiltersComponent} from './auto-group-filters/auto-group-filters.component';
import {ValidationHandlerService} from '../../../core/validation-handler.service';
import {AutoGroupFilter, AutoGroupSource, AutoGroupSourceEnum} from '../../../../models/auto-group/auto-group-setting';
import {CacheService} from '../../../services/cache/cache.service';
import {CommonConstant} from '../../../app.constants';
import {CampaignService} from '../../../services/campaign/campaign.service';
import {NewInfluencerGroup} from '../../../../models/auto-group/auto-group-setting';
import {InfluencerGroupsService} from '../../influencer-groups/influencer-groups.service';
import {BrandedSignUp} from '../../../../models/auto-group/branded-sign-up';

@Component({
  selector: 'new-group-settings',
  templateUrl: './new-group-settings.component.html',
  styleUrls: ['./new-group-settings.component.scss']
})
export class NewGroupSettingsComponent implements OnInit {

  @ViewChild('autoGroupSource') autoGroupSource: AutoGroupSourcesComponent;
  @ViewChild('autoGroupFilter') autoGroupFilter: AutoGroupFiltersComponent;

  @Input() influencerGroup: NewInfluencerGroup;
  @Input() listProject: any  = [];

  @Output() updateGroupEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() editOrCreateProjectEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() beforeSubmitGroupSetting: EventEmitter<any> = new EventEmitter<any>();
  @Output() afterSubmitGroupSetting: EventEmitter<any> = new EventEmitter<any>();

  public identifyRelevantInfluencer = false;
  public autoGroupSourceList: Array<AutoGroupDropdown> = [];
  public supportedFilterTypeOptions: Array<AutoGroupDropdown>;
  public campaignList: Array<AutoGroupDropdown>;
  public influencerGroupList: Array<AutoGroupDropdown>;
  public brandedSignUpConfig: BrandedSignUp;
  public showSourceRequiredErrorMessage: boolean;

  constructor(private userService: UserService,
              private eventTracker: EventTrackerService,
              private groupService: GroupService,
              private cacheService: CacheService,
              private validationService: ValidationHandlerService,
              private influencerGroupsService: InfluencerGroupsService,
              private campaignService: CampaignService) {
  }

  ngOnInit() {
    this.identifyRelevantInfluencer = this.influencerGroup.filters.length > 0 || this.influencerGroup.sources.length > 0;
    this.initAutoGroupSettings();
  }

  private initAutoGroupSettings() {
    this.cacheService.get(CommonConstant.NEW_AUTO_GROUP_SOURCE_KEY,
      this.groupService.getInfluencerGroupSourceTypes()).subscribe((res) => {
      this.autoGroupSourceList = res.filter(resItem => resItem.id !== AutoGroupSourceEnum.BRANDED_SIGNUP);
    });
    this.cacheService.get(CommonConstant.NEW_AUTO_GROUP_FILTER_KEY,
      this.groupService.getInfluencerGroupFilterTypes()).subscribe(res => {
      this.supportedFilterTypeOptions = res;
    });
    if (this.listProject && this.listProject.length === 0) {
      this.cacheService.get(CommonConstant.LIST_PROJECT_KEY, this.userService.getAllProjectOfAdvertiser())
        .subscribe((res: any) => {
          this.listProject = res ? res.data : [];
        });
    }
    const companyId = this.userService.getCompanyId();
    this.loadBrandedSignUpConfig(companyId);
    this.cacheService.get(`campaign_list_${companyId}`,
      this.campaignService.getCampaignShortListForCompany(companyId)).subscribe(res => {
        this.campaignList = res.map(cp => ({
          id: cp.id,
          name: cp.name,
          value: cp.name
        }));
    });
    this.cacheService.get(`group_list_${companyId}`,
      this.influencerGroupsService.getInfluencerGroupShortList(companyId)).subscribe((res) => {
        this.influencerGroupList = res.map(cp => ({
          id: cp.id,
          name: cp.name,
          value: cp.name
        }));
    });
  }

  groupSourceDataIsReady() {
    return this.autoGroupSourceList.length > 0 && this.supportedFilterTypeOptions && this.influencerGroupList && this.campaignList;
  }

  updateGroup() {
    this.updateGroupEvent.emit(this.influencerGroup);
  }

  toggleIdentifyRelevantInfluencers(isToggled) {
    this.sendMixPanelEvent(false, isToggled);
    this.showSourceRequiredErrorMessage = false;
    if (isToggled) {
      this.initGroupSourceAndFilter();
    } else {
      this.influencerGroup.filters = [];
      if (!this.brandedSignUpConfig) {
        this.influencerGroup.sources = [];
      } else {
        this.influencerGroup.sources = [this.buildBrandedSignSource(this.brandedSignUpConfig)];
      }
    }
  }

  private loadBrandedSignUpConfig(companyId) {
    const key = `${CommonConstant.BRANDED_SIGNUP_CONFIG_KEY}_${companyId}`;
    this.cacheService.get(key, this.userService.getSignUpPageData(companyId)).subscribe(res => {
      this.handleBrandedSignUpResponse(res);
    });
  }

  private handleBrandedSignUpResponse(res) {
    if (!res || (res.influencerGroupId !== this.influencerGroup.id)) {
      return;
    }
    this.brandedSignUpConfig = res;
    this.identifyRelevantInfluencer = true;
    const brandedSignUpSource = this.buildBrandedSignSource(res);
    this.influencerGroup.sources = [brandedSignUpSource].concat(this.influencerGroup.sources);
  }

  private buildBrandedSignSource(brandedSignUp: BrandedSignUp): AutoGroupSource {
    const brandedSignUpSource = new AutoGroupSource();
    brandedSignUpSource.type = AutoGroupSourceEnum.BRANDED_SIGNUP;
    brandedSignUpSource.sourceValues = [
      {
        id: brandedSignUp.id,
        name: brandedSignUp.brandName,
        type: AutoGroupSourceEnum.BRANDED_SIGNUP
      }
    ];
    return brandedSignUpSource;
  }

  private initGroupSourceAndFilter() {
    if (this.influencerGroup && this.influencerGroup.filters.length === 0) {
      const defaultFilter = new AutoGroupFilter();
      defaultFilter.index = 0;
      this.influencerGroup.filters.push(defaultFilter);
    }
    if (this.influencerGroup && this.influencerGroup.sources.length === 0) {
      const defaultSource = new AutoGroupSource();
      this.influencerGroup.sources.push(defaultSource);
    }
  }

  private sendMixPanelEvent(isUpdateGroupSetting: boolean, isToggleOn?: boolean) {
    const target = {
      id: this.influencerGroup.id,
      name: this.influencerGroup.name,
      type: !isUpdateGroupSetting ? TargetType.GROUP_DISCOVERY : TargetType.INFLUENCER_GROUP
    };
    if (!isUpdateGroupSetting) {
      this.eventTracker.trackUserAction(UserActionType.ACTION, Page.INFLUENCER_GROUP, target, {
        actionDetails: isToggleOn ? ActionDetails.IDENTIFY_RELEVANT_INFLUENCERS_ON : ActionDetails.IDENTIFY_RELEVANT_INFLUENCERS_OFF,
        source: ActionSource.GROUP_SETTINGS,
        action: UserActionType.TOGGLE
      }, undefined, null, null);
    } else {
      this.eventTracker.trackUserAction(UserActionType.ACTION, Page.INFLUENCER_GROUP, target, {
        actionDetails: ActionDetails.UPDATED_DISCOVERY_FILTERS,
        source: ActionSource.GROUP_SETTINGS,
        action: UserActionType.UPDATE,
        ...this.influencerGroup
      }, undefined, null, null);
    }
  }

  onSubmit() {
    this.showSourceRequiredErrorMessage = this.identifyRelevantInfluencer && this.influencerGroup.sources.length === 0;
    const filterErrors = this.autoGroupFilter ? this.autoGroupFilter.validateAllFilters() : [];
    const sourcesErrors = this.autoGroupSource ? this.autoGroupSource.validateAllSources() : [];

    if (filterErrors.length > 0 || sourcesErrors.length > 0) {
      this.validationService.shouldDisplayErrors(true);
      this.validationService.updateErrors(filterErrors.concat(sourcesErrors));
      return;
    }

    if (this.showSourceRequiredErrorMessage) {
      return;
    }

    this.beforeSubmitGroupSetting.emit();
    this.cacheService.clear(`InfluencerGroupDetail${this.influencerGroup.id}`);
    // call API
    this.groupService.updateInfluencerGroupV2(this.influencerGroup, this.userService.getCompanyId()).subscribe((res) => {
      // Send mixpanel event
      this.sendMixPanelEvent(true);
    }, (error) => {
      this.afterSubmitGroupSetting.emit({
        isError: true
      });
    });
  }

  editOrCreateProject(project) {
    this.editOrCreateProjectEvent.emit(project);
  }

  reloadAndSetSelectedProject(selectedProject) {
    this.userService.getAllProjectOfAdvertiser().subscribe((res: any) => {
      const currentProjectList = Array.isArray(this.listProject) ? this.listProject.map(p => p.id) : [];
      const activeProject = selectedProject.id > 0 ? selectedProject : res.data.find(p => currentProjectList.indexOf(p.id) < 0);
      if (activeProject) {
        this.updateSelectedProjectForSource(activeProject);
      }
      this.listProject = res.data;
    });
  }

  updateSelectedProjectForSource(project) {
    if (!this.autoGroupSource) {
      return;
    }
    const mentionSource = this.autoGroupSource.autoGroupSourceItemList.find(si => si.showProjectList);
    if (mentionSource) {
      mentionSource.updateSelectedProject(project);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/main/group-details/new-group-settings/new-group-settings.component.ts